"use client"

import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { ChevronDown } from "lucide-react"
import { getTokenFromStorage, getLocalIdFromToken } from "@/lib/auth"

export function scrollToSection(id: string) {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" })
}

export default function ElegantMenuHero() {
    const [currentImage, setCurrentImage] = useState(0)
    const [loaded, setLoaded] = useState(false)
    const [greeting, setGreeting] = useState("Olá")
    const [data, setData] = useState<any>(null)
    const [localId, setLocalId] = useState<string | null>(null)

    useEffect(() => {
        const token = getTokenFromStorage()
        if (token) {
            const extractedLocalId = getLocalIdFromToken(token)
            setLocalId(extractedLocalId)
        }
    }, [])

    useEffect(() => {
        const hour = new Date().getHours()
        if (hour < 12) setGreeting("Bom dia")
        else if (hour < 18) setGreeting("Boa tarde")
        else setGreeting("Boa noite")
    }, [])

    useEffect(() => {
        if (!localId) return

        const cached = localStorage.getItem(`localData_${localId}`)
        if (cached) setData(JSON.parse(cached))

        fetch(`https://api.syncmenu.ao/generic/estabelecimento/${localId}`)
            .then((res) => res.json())
            .then((json) => {
                const content = json.localDeAtendimento
                setData(content)
                localStorage.setItem(`localData_${localId}`, JSON.stringify(content))
            })
            .catch(console.error)
    }, [localId])

    useEffect(() => {
        if (!data?.secaoId?.urls || data.secaoId.urls.length <= 1) return
        const interval = setInterval(() => {
            setLoaded(false)
            setTimeout(() => {
                setCurrentImage((prev) => (prev + 1) % data.secaoId.urls.length)
            }, 400)
        }, 10000)
        return () => clearInterval(interval)
    }, [data])

    if (!data) {
        return (
            <div className="h-[68svh] min-h-[420px] w-full bg-background flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-foreground font-serif text-lg">Carregando...</p>
                </div>
            </div>
        )
    }

    const imagens = data.secaoId.urls
    const imageUrl = imagens[currentImage] || imagens[0]

    return (
        <div id="hero" className="relative h-[68svh] min-h-[420px] w-full overflow-hidden">
            {/* Imagem em ecrã total — a foto é o herói */}
            <img
                src={imageUrl || "/placeholder.svg"}
                alt={data.estabelecimentoId.nome}
                className={`absolute inset-0 object-cover w-full h-full transition-all duration-1000 ${loaded ? "opacity-100 scale-100" : "opacity-0 scale-105"
                    }`}
                onLoad={() => setLoaded(true)}
                onError={(e) => {
                    e.currentTarget.src = "/placeholder.svg"
                    setLoaded(true)
                }}
            />

            {/* Véu: escurece a base para o texto assentar, mantém a foto viva no topo */}
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/45 to-black/25" />

            {/* Conteúdo ancorado ao fundo */}
            <div className="absolute inset-x-0 bottom-0 z-10 px-5 pb-10 sm:px-8 sm:pb-12">
                <div className="mx-auto max-w-3xl">
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: loaded ? 1 : 0.4, y: 0 }}
                        transition={{ duration: 0.7 }}
                    >
                        {/* Cumprimento + secção */}
                        <div className="flex items-center gap-3 mb-3">
                            <span className="w-8 h-px bg-primary" aria-hidden="true" />
                            <p className="text-primary/90 text-[0.65rem] sm:text-xs uppercase tracking-[0.28em] font-light">
                                {greeting} · {data.secaoId.nome}
                            </p>
                        </div>

                        <h1 className="font-serif font-light text-white text-4xl sm:text-6xl leading-[1.05] tracking-tight drop-shadow-[0_2px_18px_rgba(0,0,0,0.55)]">
                            {data.estabelecimentoId.nome}
                        </h1>

                        <p className="mt-2 text-white/70 text-sm sm:text-base font-light drop-shadow-[0_1px_10px_rgba(0,0,0,0.5)]">
                            {data.nome} · {data.estabelecimentoId.telefone || "Contacte-nos"}
                        </p>

                        {/* CTA único */}
                        <motion.button
                            whileTap={{ scale: 0.97 }}
                            onClick={() => scrollToSection("menu-categorias")}
                            className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-2.5 text-sm text-primary-foreground shadow-lg shadow-black/30 hover:bg-primary/90 transition-colors"
                        >
                            Explorar Menu
                            <ChevronDown className="size-4" aria-hidden="true" />
                        </motion.button>
                    </motion.div>
                </div>
            </div>
        </div>
    )
}
